import Tracker, { SanitizeLevel } from '@openreplay/tracker';

export interface ReplayIdentity {
  email: string;
  fullName?: string;
  roles?: string;
  // Set only while impersonating, and names the target — the session itself stays filed under the
  // admin doing the clicking. See the call site in AppLayout for why.
  impersonating?: string;
}

export interface ISessionReplayService {
  init(): void;
  isEnabled(): boolean;
  setAuthenticatedUser(identity: ReplayIdentity): void;
}

class SessionReplayServiceClass implements ISessionReplayService {
  private static instance: SessionReplayServiceClass;
  private tracker: Tracker | null = null;

  private constructor() { }

  public static getInstance(): SessionReplayServiceClass {
    if (!SessionReplayServiceClass.instance) {
      SessionReplayServiceClass.instance = new SessionReplayServiceClass();
    }
    return SessionReplayServiceClass.instance;
  }

  public init(): void {
    const projectKey = import.meta.env.VITE_OPENREPLAY_PROJECT_KEY;
    const ingestPoint = import.meta.env.VITE_OPENREPLAY_INGEST_POINT;
    const mode = (import.meta.env.VITE_OPENREPLAY_MODE || 'Auto').toLowerCase();

    if (!projectKey || !ingestPoint) {
      return;
    }

    if (mode === 'off') {
      return;
    }

    if (mode !== 'on' && import.meta.env.DEV) {
      return;
    }

    this.tracker = new Tracker({
      projectKey,
      ingestPoint,
      respectDoNotTrack: false,
      defaultInputMode: 0, // InputMode.Plain — not exported from the package root
      obscureInputEmails: false,
      obscureInputNumbers: false,
      obscureInputDates: false,
      obscureTextEmails: false,
      // toggleMask flips a password input to type="text"; levels only escalate, so Hidden sticks.
      domSanitizer: (node) =>
        node instanceof HTMLInputElement && node.type === 'password'
          ? SanitizeLevel.Hidden
          : SanitizeLevel.Plain,
      // The tracker refuses to run on non-SSL origins; localhost needs the opt-out.
      __DISABLE_SECURE_MODE: import.meta.env.DEV,
    });

    this.tracker.start().then((result) => {
      if (!result.success) {
        console.error('OpenReplay did not start session:', result.reason);
        this.tracker = null;
      }
    }).catch((err) => {
      console.error('OpenReplay failed to start', err);
      this.tracker = null;
    });
  }

  public isEnabled(): boolean {
    return this.tracker !== null;
  }

  public setAuthenticatedUser({ email, fullName, roles, impersonating }: ReplayIdentity): void {
    if (!this.tracker) return;
    this.tracker.setUserID(email);
    // Keys must be registered under Preferences > Metadata per project, or they are dropped silently.
    if (fullName) {
      this.tracker.setMetadata('name', fullName);
    }
    if (roles) {
      this.tracker.setMetadata('roles', roles);
    }
    if (impersonating) {
      this.tracker.setMetadata('impersonating', impersonating);
    }
  }
}

export const SessionReplayService: ISessionReplayService = SessionReplayServiceClass.getInstance();
